import { useState } from "react";
import {
  useGetIdentity,
  useGetList,
  useListContext,
  useNotify,
  useUnselectAll,
} from "ra-core";
import { ListPlus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Group, Prospect } from "../types";

// Quick lists that always appear, created on demand.
const DEFAULT_LISTS = ["Auction", "Probate", "ALL"];

/**
 * Bulk action for the List (prospects) view: add every checkbox-selected
 * prospect to a chosen list. Reuses the groups / prospect_groups tables.
 */
export const ProspectBulkAddToList = () => {
  const { selectedIds = [] } = useListContext<Prospect>();
  const { identity } = useGetIdentity();
  const unselectAll = useUnselectAll("prospects");
  const notify = useNotify();
  const [busy, setBusy] = useState(false);

  const { data: groups = [], refetch } = useGetList<Group>("groups", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
  });

  const findOrCreateList = async (name: string): Promise<number | null> => {
    const existing = groups.find((g) => g.name === name);
    if (existing) return Number(existing.id);
    const sb = getSupabaseClient();
    const { data, error } = await sb
      .from("groups")
      .insert({ sales_id: identity?.id, name })
      .select("id")
      .single();
    if (error) {
      if (error.code === "23505") {
        const { data: ex } = await sb
          .from("groups")
          .select("id")
          .eq("sales_id", identity?.id)
          .eq("name", name)
          .limit(1);
        return ex?.[0]?.id ?? null;
      }
      throw error;
    }
    return data.id;
  };

  const addToList = async (name: string) => {
    if (!selectedIds.length || busy) return;
    setBusy(true);
    try {
      const groupId = await findOrCreateList(name);
      if (!groupId) throw new Error("no list id");
      const rows = selectedIds.map((id) => ({
        prospect_id: id,
        group_id: groupId,
        sales_id: identity?.id,
      }));
      const { error } = await getSupabaseClient()
        .from("prospect_groups")
        .upsert(rows, { onConflict: "prospect_id,group_id", ignoreDuplicates: true });
      if (error) throw error;
      notify(`Added ${selectedIds.length} to "${name}"`, { type: "info" });
      refetch();
      unselectAll();
    } catch (e) {
      notify("Could not add to list", { type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  const promptNewList = async () => {
    const name = window.prompt("New list name:")?.trim();
    if (name) await addToList(name);
  };

  const custom = groups.filter((g) => !DEFAULT_LISTS.includes(g.name));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={busy}>
          <ListPlus className="size-4 mr-1" /> Add to list
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {DEFAULT_LISTS.map((name) => (
          <DropdownMenuItem key={name} onClick={() => addToList(name)}>
            {name}
          </DropdownMenuItem>
        ))}
        {custom.length > 0 ? <DropdownMenuSeparator /> : null}
        {custom.map((g) => (
          <DropdownMenuItem key={g.id} onClick={() => addToList(g.name)}>
            {g.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={promptNewList}>
          <Plus className="size-3 mr-1" /> New list…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
